import { c as _c } from "react/compiler-runtime";
import { ProjectConfiguration, TimeRangeConfig } from '@/api/generated/model';
import { useAvailableTags } from '@/hooks/use-available-tags';
import { useConfig as useProjectConfig } from '@/hooks/use-config';
import React, { useCallback, useMemo } from 'react';
import { produce } from 'immer';
import { GroupByChipIcon } from '@/comps/groupBy/GroupByChipIcon';
import { GroupByListItem, GroupByValue } from '@/types/groupBy';
import { translateSafe } from '@/utils/translations';
import { CUSTOM_AGGREGATE_PREFIX, isTag, TAG_PREFIX } from '@/models/common/filters';
import { humanizeString } from '@/utils/string.utils';
import { useAliasedKindName } from '@/contexts/alias';
import { filterCustomAggregates } from './useGroupByItems.utils';
export const DEFAULT_NAVBAR_ITEMS: GroupByValue[] = ['topic', 'user_intent', 'negative_user_intent_v2', 'sentiment', 'emotions'];
export const DEFAULT_IGNORED_GROUPBY_ITEMS: GroupByValue[] = ['user_action'];
type UseGroupByItemsParams = {
  projectId: string;
  timeRange: TimeRangeConfig;
};
export const useGroupByItems = t0 => {
  const $ = _c(39);
  const {
    projectId,
    timeRange
  } = t0;
  const {
    data: config,
    isLoadingData: isLoadingConfig,
    updateConfig
  } = useProjectConfig(projectId, _temp, true);
  let t1;
  if ($[0] !== config || $[1] !== projectId || $[2] !== timeRange) {
    t1 = {
      projectId,
      timeRange,
      config
    };
    $[0] = config;
    $[1] = projectId;
    $[2] = timeRange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    tagItems,
    isLoading: isLoadingTags
  } = useFetchTags(t1);
  let t2;
  if ($[4] !== projectId) {
    t2 = {
      projectId
    };
    $[4] = projectId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: customCategoriesData,
    isLoading: isLoadingCustomCategories
  } = useFetchCustomAggregates(t2);
  let t3;
  if ($[6] !== customCategoriesData) {
    t3 = customCategoriesData.map(_temp2);
    $[6] = customCategoriesData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const customCategoriesItems = t3;
  const aliasKindName = useAliasedKindName();
  let t4;
  bb0: {
    if (isLoadingTags || isLoadingConfig || isLoadingCustomCategories) {
      let t5;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    if ($[9] !== aliasKindName || $[10] !== config || $[11] !== customCategoriesItems || $[12] !== tagItems) {
      const defaultItems = getDefaultItems();
      let t6;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <GroupByChipIcon value="user_action" />;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      let t7;
      if ($[15] !== config) {
        t7 = (a, b) => groupBySortFunction(config)(a.value, b.value);
        $[15] = config;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      let t8;
      if ($[17] !== config) {
        t8 = item => setExcludedItems(config)(item);
        $[17] = config;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      let t9;
      if ($[19] !== aliasKindName) {
        t9 = item_0 => {
          const aliasedTitle = aliasKindName(item_0.value);
          return aliasedTitle ? {
            ...item_0,
            title: aliasedTitle
          } : item_0;
        };
        $[19] = aliasKindName;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      t5 = defaultItems.concat(tagItems).concat([{
        value: "user_action",
        title: translateSafe("platform", "userActions"),
        Icon: t6,
        tooltip: "User actions show WHAT users are actively doing in each conversation, allowing you to understand specific task objectives."
      }]).concat(customCategoriesItems).sort(t7).map(t8).map(t9);
      $[9] = aliasKindName;
      $[10] = config;
      $[11] = customCategoriesItems;
      $[12] = tagItems;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = t5;
  }
  const groupByItems = t4;
  let t5;
  if ($[21] !== config || $[22] !== projectId || $[23] !== updateConfig) {
    t5 = value => {
      if (!config || !projectId) {
        return;
      }
      const newConfig = produce(config, draft => {
        if (!draft.user_intelligence) {
          draft.user_intelligence = {
            group_by_ignore: [],
            group_by_sort: DEFAULT_NAVBAR_ITEMS,
            vertical_navbar_items: DEFAULT_NAVBAR_ITEMS
          };
        }
        if (!draft.user_intelligence.vertical_navbar_items) {
          draft.user_intelligence.vertical_navbar_items = DEFAULT_NAVBAR_ITEMS;
        }
        if (!draft.user_intelligence.group_by_sort || !draft.user_intelligence.group_by_sort.length) {
          draft.user_intelligence.group_by_sort = draft.user_intelligence.vertical_navbar_items;
        }
        if (draft.user_intelligence.vertical_navbar_items.includes(value)) {
          draft.user_intelligence.vertical_navbar_items = draft.user_intelligence.vertical_navbar_items.filter(e => e !== value);
        } else {
          draft.user_intelligence.vertical_navbar_items.push(value);
        }
      });
      updateConfig({
        data: newConfig,
        params: {
          project_id: projectId
        }
      });
    };
    $[21] = config;
    $[22] = projectId;
    $[23] = updateConfig;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const updateVerticalNavbarItemVisibility = t5;
  let t6;
  if ($[25] !== config || $[26] !== groupByItems || $[27] !== projectId || $[28] !== updateConfig) {
    t6 = (idx1, idx2) => {
      if (!groupByItems) {
        return;
      }
      let newSort = [];
      if (idx1 < 0 || idx2 < 0 || idx1 >= groupByItems.length || idx2 >= groupByItems.length) {
        return;
      }
      newSort = groupByItems.map(_temp3);
      const [removed] = newSort.splice(idx1, 1);
      newSort.splice(idx2, 0, removed);
      if (!config || !projectId) {
        return;
      }
      const newConfig_0 = produce(config, draft_0 => {
        if (!draft_0.user_intelligence) {
          draft_0.user_intelligence = {
            group_by_ignore: [],
            group_by_sort: [],
            vertical_navbar_items: []
          };
        }
        draft_0.user_intelligence.group_by_sort = newSort;
      });
      updateConfig({
        data: newConfig_0,
        params: {
          project_id: projectId
        }
      });
    };
    $[25] = config;
    $[26] = groupByItems;
    $[27] = projectId;
    $[28] = updateConfig;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  const updateSort = t6;
  const t7 = config?.user_intelligence;
  const t8 = isLoadingConfig || isLoadingTags;
  let t9;
  if ($[30] !== customCategoriesData) {
    t9 = customCategoriesData ?? [];
    $[30] = customCategoriesData;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== groupByItems || $[33] !== t7 || $[34] !== t8 || $[35] !== t9 || $[36] !== updateSort || $[37] !== updateVerticalNavbarItemVisibility) {
    t10 = {
      groupByItems,
      config: t7,
      updateVerticalNavbarItemVisibility,
      updateSort,
      isLoading: t8,
      customCategoriesData: t9
    };
    $[32] = groupByItems;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = updateSort;
    $[37] = updateVerticalNavbarItemVisibility;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  return t10;
};
export const useFetchCustomAggregates = t0 => {
  const $ = _c(7);
  const {
    projectId
  } = t0;
  const {
    data: projectConfig,
    isLoadingData: isLoadingConfig
  } = useProjectConfig(projectId, _temp4, true);
  let t1;
  if ($[0] !== projectConfig?.aggregates) {
    t1 = projectConfig?.aggregates ?? [];
    $[0] = projectConfig?.aggregates;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const aggregates = t1;
  let t2;
  if ($[2] !== aggregates) {
    t2 = filterCustomAggregates(aggregates);
    $[2] = aggregates;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const filtered = t2;
  let t3;
  if ($[4] !== filtered || $[5] !== isLoadingConfig) {
    t3 = {
      data: filtered,
      isLoading: isLoadingConfig
    };
    $[4] = filtered;
    $[5] = isLoadingConfig;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const useHasCategoryEnabled = t0 => {
  const $ = _c(5);
  const {
    projectId
  } = t0;
  const {
    data: projectConfig,
    isLoadingData: isLoading
  } = useProjectConfig(projectId, _temp5, true);
  let t1;
  if ($[0] !== projectConfig?.aggregates) {
    t1 = projectConfig?.aggregates?.some(_temp7) ?? false;
    $[0] = projectConfig?.aggregates;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasCategoryEnabled = t1;
  let t2;
  if ($[2] !== hasCategoryEnabled || $[3] !== isLoading) {
    t2 = {
      hasCategoryEnabled,
      isLoading
    };
    $[2] = hasCategoryEnabled;
    $[3] = isLoading;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
function getDefaultItems(): GroupByListItem<GroupByValue>[] {
  return [{
    value: 'topic',
    title: translateSafe('platform', 'topics'),
    Icon: <GroupByChipIcon value={'topic'} />,
    tooltip: 'Broad categories or subjects that are discussed during user interactions. Topics help in identifying the general areas of interest or concern for users when engaging with the platform.'
  }, {
    value: 'user_intent',
    title: translateSafe('platform', 'userIntents'),
    Icon: <GroupByChipIcon value="user_intent" />,
    tooltip: 'User intents provide insight into WHY users are initiating these conversations, helping identify whether they are gathering information, planning to buy, seeking support, or providing feedback.'
  }, {
    value: 'negative_user_intent_v2',
    title: translateSafe('platform', 'riskyBehaviors'),
    Icon: <GroupByChipIcon value={'negative_user_intent_v2'} />,
    tooltip: 'Notifications about dangerous or critical user inquiries, including sensitive content (hate, sex crimes, child exploitation, violent crimes, self-harm, illegal drugs, harassment and bullying, radicalization, sexual content), and user complaints about the business.'
  }, {
    value: 'sentiment',
    title: translateSafe('platform', 'sentiment'),
    Icon: <GroupByChipIcon value="sentiment" />,
    tooltip: 'Sentiment represents the emotional tone conveyed in a piece of text. User sentiment categorizes this tone as very positive, positive, negative, very negative, mixed or neutral.'
  }, {
    value: 'emotions',
    title: translateSafe('platform', 'emotion'),
    Icon: <GroupByChipIcon value="emotions" />,
    tooltip: 'With user emotions you can analyse the different moods the users have while interacting with the assistant.'
  }, {
    value: 'data_source',
    title: translateSafe('platform', 'dataSource'),
    Icon: <GroupByChipIcon value="data_source" />,
    tooltip: ''
  }, {
    value: 'keyword',
    title: translateSafe('platform', 'keywords'),
    Icon: <GroupByChipIcon value={'keyword'} />,
    tooltip: 'Specific words or phrases frequently used by users during interactions. Keywords are extracted to understand the primary language and terminology that users employ, which aids in improving the relevance and accuracy of the LLM’s responses.'
  }];
}
function setExcludedItems(projectConfig: ProjectConfiguration | undefined) {
  return (item: GroupByListItem<GroupByValue>) => ({
    ...item,
    ignored: (projectConfig?.user_intelligence?.group_by_ignore ?? []).includes(item.value)
  });
}

/**
 * sort by the order provided
 * by the user (saved in the project config)
 *
 * @param projectConfig
 */
export function groupBySortFunction(projectConfig: ProjectConfiguration | undefined) {
  return (a: string, b: string) => {
    // if no config or no sort order is defined, maintain original order
    if (!projectConfig?.user_intelligence?.group_by_sort?.length) {
      return 0;
    }
    const sortOrder = projectConfig.user_intelligence.group_by_sort;
    const aIndex = sortOrder.indexOf(a);
    const bIndex = sortOrder.indexOf(b);

    // if both items are not in sort order, maintain their relative order
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }

    // if one item is not in sort order, put it at the end
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    // if both items are in sort order, sort by their positions
    return aIndex - bIndex;
  };
}
type FetchTagsParams = {
  projectId: string;
  timeRange: TimeRangeConfig;
  config: ProjectConfiguration | undefined;
};

/**
 * if a tag is set to be shown in the navbar,
 * then also show it in the groupby bar,
 * regardless of the availability of useAvailableTags()
 *
 * @param projectId
 * @param timeRange
 * @param config
 */
function useFetchTags(t0) {
  const $ = _c(13);
  const {
    projectId,
    timeRange,
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    availableTags,
    isLoading
  } = useAvailableTags(projectId, t1, timeRange);
  let t2;
  if ($[1] !== availableTags?.tags || $[2] !== config?.user_intelligence?.vertical_navbar_items) {
    const navbarTags = config?.user_intelligence?.vertical_navbar_items?.filter(isTag).map(_temp8) ?? [];
    let t3;
    if ($[4] !== availableTags?.tags) {
      t3 = availableTags?.tags ?? [];
      $[4] = availableTags?.tags;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = new Set([...navbarTags, ...t3]);
    $[1] = availableTags?.tags;
    $[2] = config?.user_intelligence?.vertical_navbar_items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const tagsSet = t2;
  let t3;
  if ($[6] !== tagsSet) {
    t3 = Array.from(tagsSet);
    $[6] = tagsSet;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const finalTags = t3;
  let t4;
  if ($[8] !== finalTags) {
    t4 = finalTags.map(_temp9);
    $[8] = finalTags;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const tagItems = t4;
  let t5;
  if ($[10] !== isLoading || $[11] !== tagItems) {
    t5 = {
      tagItems,
      isLoading
    };
    $[10] = isLoading;
    $[11] = tagItems;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp9(t) {
  const value = `${TAG_PREFIX}${t}` as const;
  return {
    title: humanizeString(t),
    value: value as GroupByValue,
    Icon: <GroupByChipIcon value={value} />
  };
}
function _temp8(f) {
  return f.replace(TAG_PREFIX, "");
}
function _temp() {
  return null;
}
function _temp2(cat) {
  return {
    title: cat.display_name,
    value: `${CUSTOM_AGGREGATE_PREFIX}${cat.category_name}` as GroupByValue,
    Icon: <GroupByChipIcon value="data_source" />
  };
}
function _temp3(e_0) {
  return e_0.value;
}
function _temp4() {
  return null;
}
function _temp5() {
  return null;
}
function _temp6(c) {
  return c.kind === "topic" && c.children && c.children.length > 0;
}
function _temp7(a) {
  return a.category_name === "topic" && a.kind === "topic" && a.children?.some(_temp6);
}